import { createApi } from '@reduxjs/toolkit/query/react'
import type {
  ResourceAccessGroupGrant,
  ResourceAccessLevel,
  ResourceAccessLookup,
  ResourceAccessLookupQuery,
  ResourceAccessRequest,
  ResourceAccessScope,
  ResourceAccessScopeWithGrants,
  ResourceAccessUserGrant
} from '@atbash-labs/proxy-client'

import { getOntologyServiceBaseUrl } from '../../config'
import { ontologyBaseQuery } from '../core'
import { notificationsApi } from '../notifications/notifications-api'

const ONTOLOGY_SERVICE_URL = getOntologyServiceBaseUrl()
const scopePath = (scopeId: string) =>
  `${ONTOLOGY_SERVICE_URL}/resource-access/${encodeURIComponent(scopeId)}`

const lookupTags = (result: ResourceAccessLookup | undefined) =>
  result?.scopeId
    ? [
        { type: 'ResourceAccess' as const, id: result.scopeId },
        { type: 'ResourceAccess' as const, id: 'LOOKUP' }
      ]
    : [{ type: 'ResourceAccess' as const, id: 'LOOKUP' }]

export const resourceAccessApi = createApi({
  reducerPath: 'resourceAccessApi',
  baseQuery: ontologyBaseQuery,
  tagTypes: ['ResourceAccess'],
  endpoints: (builder) => ({
    lookupResourceAccess: builder.query<ResourceAccessLookup, ResourceAccessLookupQuery>({
      query: ({ resourceType, path, id }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/resource-access/lookup`,
        params: { resourceType, path, id }
      }),
      providesTags: (result) => lookupTags(result)
    }),
    createResourceAccessRequest: builder.mutation<
      ResourceAccessRequest,
      { scopeId: string; message: string }
    >({
      query: ({ scopeId, message }) => ({
        url: `${scopePath(scopeId)}/requests`,
        method: 'POST',
        body: { message }
      }),
      invalidatesTags: (_result, _error, { scopeId }) => [
        { type: 'ResourceAccess', id: scopeId },
        { type: 'ResourceAccess', id: 'LOOKUP' }
      ]
    }),
    grantAccessRequest: builder.mutation<
      ResourceAccessRequest,
      { requestId: string; accessLevel: ResourceAccessLevel }
    >({
      query: ({ requestId, accessLevel }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/resource-access/requests/${encodeURIComponent(requestId)}/grant`,
        method: 'POST',
        body: { accessLevel }
      }),
      invalidatesTags: [{ type: 'ResourceAccess', id: 'LOOKUP' }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(notificationsApi.util.invalidateTags([{ type: 'Notification', id: 'LIST' }]))
        } catch {
          // Leave the notification open so the owner can retry.
        }
      }
    }),
    denyAccessRequest: builder.mutation<ResourceAccessRequest, { requestId: string }>({
      query: ({ requestId }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/resource-access/requests/${encodeURIComponent(requestId)}/deny`,
        method: 'POST'
      }),
      invalidatesTags: [{ type: 'ResourceAccess', id: 'LOOKUP' }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(notificationsApi.util.invalidateTags([{ type: 'Notification', id: 'LIST' }]))
        } catch {
          // Leave the notification open so the owner can retry.
        }
      }
    }),
    getResourceAccess: builder.query<ResourceAccessScopeWithGrants, string>({
      query: (scopeId) => scopePath(scopeId),
      providesTags: (_result, _error, scopeId) => [{ type: 'ResourceAccess', id: scopeId }]
    }),
    transferResourceAccessOwner: builder.mutation<
      ResourceAccessScope,
      { scopeId: string; newOwnerUserId: string }
    >({
      query: ({ scopeId, newOwnerUserId }) => ({
        url: `${scopePath(scopeId)}/owner`,
        method: 'PATCH',
        body: { newOwnerUserId }
      }),
      invalidatesTags: (_result, _error, { scopeId }) => [{ type: 'ResourceAccess', id: scopeId }]
    }),
    addResourceAccessUserGrant: builder.mutation<
      ResourceAccessScopeWithGrants,
      { scopeId: string; grant: ResourceAccessUserGrant }
    >({
      query: ({ scopeId, grant }) => ({
        url: `${scopePath(scopeId)}/grants/users`,
        method: 'POST',
        body: grant
      }),
      invalidatesTags: (_result, _error, { scopeId }) => [{ type: 'ResourceAccess', id: scopeId }]
    }),
    removeResourceAccessUserGrant: builder.mutation<
      ResourceAccessScopeWithGrants,
      { scopeId: string; userId: string }
    >({
      query: ({ scopeId, userId }) => ({
        url: `${scopePath(scopeId)}/grants/users/${encodeURIComponent(userId)}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, { scopeId }) => [{ type: 'ResourceAccess', id: scopeId }]
    }),
    addResourceAccessGroupGrant: builder.mutation<
      ResourceAccessScopeWithGrants,
      { scopeId: string; grant: ResourceAccessGroupGrant }
    >({
      query: ({ scopeId, grant }) => ({
        url: `${scopePath(scopeId)}/grants/groups`,
        method: 'POST',
        body: grant
      }),
      invalidatesTags: (_result, _error, { scopeId }) => [{ type: 'ResourceAccess', id: scopeId }]
    }),
    removeResourceAccessGroupGrant: builder.mutation<
      ResourceAccessScopeWithGrants,
      { scopeId: string; groupId: string }
    >({
      query: ({ scopeId, groupId }) => ({
        url: `${scopePath(scopeId)}/grants/groups/${encodeURIComponent(groupId)}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, { scopeId }) => [{ type: 'ResourceAccess', id: scopeId }]
    })
  })
})

export const {
  useLookupResourceAccessQuery,
  useCreateResourceAccessRequestMutation,
  useGrantAccessRequestMutation,
  useDenyAccessRequestMutation,
  useGetResourceAccessQuery,
  useTransferResourceAccessOwnerMutation,
  useAddResourceAccessUserGrantMutation,
  useRemoveResourceAccessUserGrantMutation,
  useAddResourceAccessGroupGrantMutation,
  useRemoveResourceAccessGroupGrantMutation
} = resourceAccessApi
